import { useCallback, useEffect, useRef } from "react";

import { useLoadFinderStore } from "@/state/loadFinderStore";
import { buildNewLoadsNotification } from "@/utils/newLoadsNotification";

const NOTIFICATION_ICON = "/android-chrome-192x192.png";

const isSupported = () => typeof window !== "undefined" && "Notification" in window;

const isAppInView = () => document.visibilityState === "visible" && document.hasFocus();

const truckLabel = ({ driverName, truckName }) => {
  if (driverName && truckName && driverName !== truckName) return `${driverName} (${truckName})`;
  return driverName || truckName;
};

const askPermission = () => {
  if (Notification.permission !== "default") return;
  Promise.resolve(Notification.requestPermission()).catch(() => {});
};

export const useNewLoadBrowserNotifications = (onOpenFilter) => {
  const onOpenFilterRef = useRef(null);

  useEffect(() => {
    onOpenFilterRef.current = onOpenFilter;
    return () => {
      onOpenFilterRef.current = null;
    };
  }, [onOpenFilter]);

  // Firefox and Safari ignore a permission request made outside a user gesture,
  // so the request on open is repeated on the first click.
  useEffect(() => {
    if (!isSupported() || Notification.permission !== "default") return;
    askPermission();
    document.addEventListener("click", askPermission, { once: true });
    return () => document.removeEventListener("click", askPermission);
  }, []);

  return useCallback((newLoadsByFilter) => {
    if (!isSupported() || Notification.permission !== "granted" || isAppInView()) return;

    const { truckFilters, trucks, companyTrucks } = useLoadFinderStore.getState();
    const allTrucks = [...(trucks.data ?? []), ...(companyTrucks.data ?? [])];

    Object.entries(newLoadsByFilter).forEach(([filterKey, loads]) => {
      const filter = (truckFilters.data ?? []).find((f) => String(f.id) === filterKey);
      const truck = filter && allTrucks.find((t) => t.truckId === filter.truckId);
      if (!truck) return;

      const { title, body } = buildNewLoadsNotification(truckLabel(truck), loads);
      try {
        const notification = new Notification(title, {
          body,
          icon: NOTIFICATION_ICON,
          tag: `load-finder-filter-${filterKey}`,
          renotify: true,
        });
        notification.onclick = () => {
          window.focus();
          notification.close();
          onOpenFilterRef.current?.(filter);
        };
      } catch {
        // Mobile browsers only allow notifications from a service worker and throw here.
        return;
      }
    });
  }, []);
};
